import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { Toaster } from "@/components/ui";
import { Layout } from "@/components/layout";
import { ProtectedRoute } from "@/components/common";
import { startOfflineSync, stopOfflineSync } from "@/lib/offline/sync";
import { useAuthStore } from "@/stores";

import { LoginPage, UsersPage, RolesPage } from "@/features/auth";
import { POSPage } from "@/features/pos";
import { OrdersPage, InvoicePage, PendingOrdersPage } from "@/features/orders";
import {
  ProductsPage,
  ProductDetailsPage,
  AttributesPage,
  CategoriesPage,
} from "@/features/products";
import { CustomersPage } from "@/features/customers";
import { InventoryPage } from "@/features/inventory";
import { ShiftsPage } from "@/features/shifts";
import {
  SettingsPage,
  PromotionsPage,
} from "@/features/settings";
import { KitchenDisplayPage } from "@/features/kitchen";
import { RefundsPage } from "@/features/refunds";
import { BackupPage } from "@/features/data-io";
import { ReportsPage, EndOfDayPage } from "@/features/reports";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24,
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      networkMode: "always",
    },
  },
});

const persister = createSyncStoragePersister({
  storage: typeof window === "undefined" ? undefined : window.localStorage,
  key: "coffee-pos-rq-cache",
});

function OfflineSyncManager() {
  useEffect(() => {
    startOfflineSync(queryClient);

    return () => {
      stopOfflineSync();
    };
  }, []);

  return null;
}

function AuthCacheGuard() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const queryClient = useQueryClient();
  const previousIdRef = useRef<string | null>(userId);

  useEffect(() => {
    if (previousIdRef.current !== userId) {
      queryClient.clear();
      previousIdRef.current = userId;
    }
  }, [userId, queryClient]);

  return null;
}

export default function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * 7,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => query.state.status === "success",
        },
      }}
    >
      <OfflineSyncManager />
      <AuthCacheGuard />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/pos" replace />} />
            <Route path="/pos" element={<POSPage />} />
            <Route
              path="/orders"
              element={
                <ProtectedRoute requiredPermission="orders.read">
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/pending"
              element={
                <ProtectedRoute requiredPermission="orders.read">
                  <PendingOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id/invoice"
              element={
                <ProtectedRoute requiredPermission="orders.read">
                  <InvoicePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/refunds"
              element={
                <ProtectedRoute requiredPermission="refunds.read">
                  <RefundsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute requiredPermission="products.read">
                  <ProductsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id"
              element={
                <ProtectedRoute requiredPermission="products.read">
                  <ProductDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attributes"
              element={
                <ProtectedRoute requiredPermission="products.read">
                  <AttributesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute requiredPermission="products.read">
                  <CategoriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute requiredPermission="customers.read">
                  <CustomersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute requiredPermission="inventory.read">
                  <InventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shifts"
              element={
                <ProtectedRoute requiredPermission="shifts.read">
                  <ShiftsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermission="reports.read">
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/end-of-day"
              element={
                <ProtectedRoute requiredPermission="reports.read">
                  <EndOfDayPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredPermission="settings.read">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/promotions"
              element={
                <ProtectedRoute requiredPermission="settings.read">
                  <PromotionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermission="users.read">
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute requiredPermission="roles.read">
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/backup"
              element={
                <ProtectedRoute
                  requiredAnyPermission={["data.export", "data.import"]}
                >
                  <BackupPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/kitchen"
            element={
              <ProtectedRoute>
                <KitchenDisplayPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/pos" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </PersistQueryClientProvider>
  );
}
